import { fetchAllPagesWithRateLimit } from './fetch-all-pages';
import { RateLimiter } from './rate-limiter';

/**
 * Claim IDs per request. GET /claim pages at most 100 (`400 … must be less than or equal to 100`),
 * and 100 UUIDs keep a `filterClaimId` URL under CloudFront's 8,192-byte limit (414 measured at
 * ~184 IDs).
 */
export const CLAIM_ID_CHUNK_SIZE = 100;
/** Where a collection stops by default — a range covering most of an account is not a filter. */
export const DEFAULT_MAX_CLAIMS = 5000;

const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_REQUESTS_PER_MINUTE = 100;
const RATE_LIMIT_RETRIES = 3;
const RATE_LIMIT_BACKOFF_MS = 5000;
const FOREIGN_ABORT_RETRIES = 5;

export interface ICollectByClaimIdsProgress<T> {
    /** Unique items so far, in arrival order. */
    items: T[];
    claimsLoaded: number;
    /** Claims this collection will read: the server total, clipped to `maxClaims`. */
    claimsTotal: number;
    /** The server reported more claims than `maxClaims`; the rest were never read. */
    claimsCapped: boolean;
    done: boolean;
}

export interface ICollectByClaimIdsOptions<T> {
    /** One page of the claim query (0-indexed). `total` is the claims' `pagination-total`. */
    fetchClaimPage: (page: number, pageSize: number) => Promise<{ ids: string[]; total: number }>;
    /** One page of the items linked to `claimIds` (at most {@link CLAIM_ID_CHUNK_SIZE}). */
    fetchItemPage: (claimIds: string[], page: number, pageSize: number) => Promise<{ items: T[]; total: number }>;
    getId?: (item: T) => string | undefined;
    /** After each claim chunk's items are in, and once more with `done: true`. */
    onProgress?: (progress: ICollectByClaimIdsProgress<T>) => void;
    maxClaims?: number;
    /** Requests in flight at once, claim and item requests together. */
    concurrency?: number;
    maxRequestsPerMinute?: number;
    /** Share one budget across collections that run together. */
    rateLimiter?: RateLimiter;
    signal?: AbortSignal;
    /** First 429 backoff; doubles per retry. */
    rateLimitBackoffMs?: number;
}

const statusOf = (error: unknown): number | undefined => {
    if (typeof Response !== 'undefined' && error instanceof Response) {
        return error.status;
    }
    const status = (error as { status?: unknown } | null)?.status;
    return typeof status === 'number' ? status : undefined;
};

const isAbortError = (error: unknown): boolean => (error as { name?: unknown } | null)?.name === 'AbortError';

/**
 * Retry a request that failed for a reason that is not about this collection:
 *
 * - **429** — pause every request on the shared limiter, not only this one, then retry.
 * - **Aborted by someone else** — the app tags every request with one global request group, and
 *   other code cancels groups: the Tasks page on a list reload (`cancelGroup('messaging-list')`),
 *   the sidebar whenever it re-renders (`cancelGroup('messaging')`). Seen in the browser
 *   2026-10-12: a reload after Clear aborted an in-flight collection request, and without this
 *   retry that one abort ends the whole shared collection. Only this collection's own `signal`
 *   stops it.
 */
async function withRetry<R>(run: () => Promise<R>, limiter: RateLimiter, backoffMs: number, signal?: AbortSignal): Promise<R> {
    let rateLimited = 0;
    let foreignAborts = 0;
    while (true) {
        try {
            return await run();
        } catch (error) {
            if (signal?.aborted) {
                throw error;
            }
            if (statusOf(error) === 429 && rateLimited < RATE_LIMIT_RETRIES) {
                limiter.pauseUntil(Date.now() + backoffMs * 2 ** rateLimited);
                rateLimited += 1;
            } else if (isAbortError(error) && foreignAborts < FOREIGN_ABORT_RETRIES) {
                foreignAborts += 1;
            } else {
                throw error;
            }
            await limiter.acquire(signal);
        }
    }
}

/**
 * One request on `limiter`'s budget, retried like a collection's own requests (see `withRetry`) —
 * for a caller that pages something itself but must share the budget, e.g. the Coding result
 * search resolving its claim IDs before the task collection starts.
 */
export async function rateLimitedRequest<R>(run: () => Promise<R>, limiter: RateLimiter, signal?: AbortSignal): Promise<R> {
    await limiter.acquire(signal);
    return withRetry(run, limiter, RATE_LIMIT_BACKOFF_MS, signal);
}

/** At most `limit` of the wrapped calls run at once; the rest wait their turn. */
function createSlots(limit: number): <R>(run: () => Promise<R>) => Promise<R> {
    let active = 0;
    const waiting: Array<() => void> = [];
    return async run => {
        if (active < limit) {
            active += 1;
        } else {
            // The slot is handed over directly by the finishing call, so `active` stays counted.
            await new Promise<void>(resolve => waiting.push(resolve));
        }
        try {
            return await run();
        } finally {
            const next = waiting.shift();
            if (next) {
                next();
            } else {
                active -= 1;
            }
        }
    };
}

/**
 * Collect the items linked to every claim a claim query returns: page the claims 100 at a time
 * and, for each page, page the items filtered by those claim IDs — the route for any task filter
 * GET /message cannot express itself (DOS, payer, …). The backend's recommended shape (2026-10-08):
 * load as claim pages arrive and deduplicate, since one item can link to claims in two chunks.
 *
 * One concurrency limit and one sliding-window rate limit across both levels — the claim pager
 * prefetches while a chunk's items load, so two separate limits would double the requests in
 * flight. A 429 pauses everything and retries; so does an abort that did not come from `signal`
 * (see `withRetry`). Any other failure rejects — the partial result was already reported through
 * `onProgress`, so a caller can tell "loaded some, then failed" from "nothing matches".
 */
export async function collectByClaimIds<T>(options: ICollectByClaimIdsOptions<T>): Promise<ICollectByClaimIdsProgress<T>> {
    const maxClaims = options.maxClaims ?? DEFAULT_MAX_CLAIMS;
    const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
    const getId = options.getId ?? ((item: T) => (item as { id?: string })?.id);
    const { signal } = options;
    const backoffMs = options.rateLimitBackoffMs ?? RATE_LIMIT_BACKOFF_MS;
    const limiter =
        options.rateLimiter ??
        new RateLimiter({
            maxRequests: options.maxRequestsPerMinute ?? DEFAULT_MAX_REQUESTS_PER_MINUTE,
            windowMs: 60_000,
        });

    const slot = createSlots(concurrency);
    // Once a slot is granted, re-check before sending. The pager takes its rate-limit permit before
    // the request queues for a slot, so a queued request would otherwise go out regardless of:
    // - a 429 pause another request set meanwhile (it must wait the pause out like everyone else);
    // - a stop that happened meanwhile.
    const request = <R>(run: () => Promise<R>): Promise<R> =>
        withRetry(
            () =>
                slot(async () => {
                    await limiter.waitUntilReady(signal);
                    if (signal?.aborted) {
                        throw new DOMException('Claim collection stopped', 'AbortError');
                    }
                    try {
                        return await run();
                    } catch (error) {
                        // Pause BEFORE the slot is handed on — `withRetry` sets it only after the slot
                        // is released, which is too late for the request already waiting for it.
                        if (statusOf(error) === 429) {
                            limiter.pauseUntil(Date.now() + backoffMs);
                        }
                        throw error;
                    }
                }),
            limiter,
            backoffMs,
            signal,
        );

    const items: T[] = [];
    const seenItems = new Set<string>();
    let serverTotal = 0;
    let claimsLoaded = 0;

    const snapshot = (done: boolean): ICollectByClaimIdsProgress<T> => ({
        items: items.slice(),
        claimsLoaded,
        claimsTotal: Math.min(serverTotal, maxClaims),
        claimsCapped: serverTotal > maxClaims,
        done,
    });

    await fetchAllPagesWithRateLimit<string>({
        pageSize: CLAIM_ID_CHUNK_SIZE,
        concurrency,
        rateLimiter: limiter,
        signal,
        getId: id => id,
        retainItems: false,
        fetchPage: async (page, pageSize) => {
            const result = await request(() => options.fetchClaimPage(page, pageSize));
            serverTotal = result.total;
            // Clipping the total is what stops the pager at the cap.
            return { items: result.ids, total: Math.min(result.total, maxClaims) };
        },
        onPage: async claimIds => {
            const chunk = claimIds.slice(0, Math.max(0, maxClaims - claimsLoaded));
            if (chunk.length) {
                await fetchAllPagesWithRateLimit<T>({
                    pageSize: CLAIM_ID_CHUNK_SIZE,
                    concurrency,
                    rateLimiter: limiter,
                    signal,
                    getId,
                    retainItems: false,
                    fetchPage: (page, pageSize) => request(() => options.fetchItemPage(chunk, page, pageSize)),
                    onPage: pageItems => {
                        for (const item of pageItems) {
                            const id = getId(item);
                            if (id) {
                                if (seenItems.has(id)) {
                                    continue;
                                }
                                seenItems.add(id);
                            }
                            items.push(item);
                        }
                    },
                });
            }
            claimsLoaded += chunk.length;
            options.onProgress?.(snapshot(false));
        },
    });

    const result = snapshot(true);
    options.onProgress?.(result);
    return result;
}
